/**
 * PATH: packages/ui/src/kanban/components/card-detail/CommentsSection.tsx
 * FILE_ID: UI-KANBAN-CMP-115
 * PURPOSE: Trello-style human comments feed (card notes) — composer + newest-
 *   first list with edit/delete rows (Pencil + Trash2 on hover).
 * WHY: Split from the CardDetailDrawer monolith (UAT P5). Edit affordance
 *   added 2026-07-16 (BUG-2026-07-16-KANBAN-CARD-NOTES-NO-EDIT) via
 *   CommentNoteRow; icon clarity
 *   BUG-2026-07-16-KANBAN-CARD-NOTES-EDIT-DELETE-ICONS. PATCH /comments/<id>/
 *   already existed.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Loader2, MessageSquare } from 'lucide-react'
import type { KanbanApi, KanbanMember } from '../../api'
import { MentionTextarea } from '../MentionTextarea'
import type { Card } from '../../types'
import { deriveMemberMentionIds } from '../../mentionTokens'
import {
  commentDraftScope,
  migrateAnonCommentDraft,
  readCommentDraft,
  useCommentDraft,
} from './commentDraft'
import { CommentNoteRow } from './CommentNoteRow'

// =============================================================================
// CommentsSection — Trello-style activity feed for human comments
// =============================================================================

export function CommentsSection({
  card,
  api,
  qc,
  boardId,
  members,
  currentUserId,
}: {
  card: Card
  api: KanbanApi
  qc: ReturnType<typeof useQueryClient>
  /** Audit P1-7 (Arc 3) — scope cache invalidation to this board.
   *  See ChecklistsSection for the same pattern + rationale. */
  boardId?: string
  /** Org member directory — the SAME `KanbanMember[]` the Teams thread
   *  uses. Lets a note author resolve to a name even before the backend
   *  ships `author_name`, and NEVER renders a raw `User #<id>`
   *  (BUG-2026-07-KANBAN-CARD-NOTE-AUTHOR-ID). */
  members?: KanbanMember[]
  /** Current viewer id — scopes the LOCAL comment draft so it is never visible
   *  to another user on a shared profile. Absent → device-scoped 'anon'. */
  currentUserId?: string | number | null
}) {
  // Local-only draft (per-user + per-card). Seed from any saved draft so an
  // in-progress note survives closing/reopening the drawer; mirror keystrokes
  // into the store and purge on a successful send.
  const { draft, setDraft, clearDraft } = useCommentDraft(currentUserId, card.id)
  const [body, setBody] = useState(draft)
  // Stale-body guard: if this section is reused for a different card OR the
  // draft-key user segment changes (anon→realId hydrate), `body` would
  // otherwise keep the previous identity's half-typed text (it only seeds on
  // first mount). Re-seed from the saved draft on scope change ONLY, so active
  // typing for the SAME card+user is never clobbered.
  const draftScope = commentDraftScope(currentUserId, card.id)
  const lastDraftScopeRef = useRef(draftScope)
  if (lastDraftScopeRef.current !== draftScope) {
    lastDraftScopeRef.current = draftScope
    migrateAnonCommentDraft(currentUserId, card.id)
    setBody(readCommentDraft(currentUserId, card.id))
  }

  const resolveMember = useMemo(() => {
    const byId = new Map<number, KanbanMember>()
    for (const m of members ?? []) byId.set(m.id, m)
    return (id: number) => byId.get(id)
  }, [members])

  const q = useQuery({
    queryKey: ['kanban', 'card', card.id, 'comments'],
    queryFn: () => api.listComments(card.id),
    // BUG-2026-07-07-KANBAN-CARD-404-TOAST-SPAM — a 4xx (e.g. the card's
    // lane was soft-deleted so this sub-resource 404s "No Card matches the
    // given query") will never succeed on retry: retrying only re-fires the
    // error toast and keeps `isLoading` true, which is what left the Card
    // tab stuck on "Loading…". Retry only transient (5xx / network) failures.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status
      if (status !== undefined && status >= 400 && status < 500) return false
      return failureCount < 2
    },
  })

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id, 'comments'] })
    // Audit P1-7 fix — see ChecklistsSection.invalidateAll for rationale.
    if (boardId) {
      qc.invalidateQueries({ queryKey: ['kanban', 'cards', boardId] })
    } else {
      qc.invalidateQueries({ queryKey: ['kanban', 'cards'] })
    }
  }

  const post = useMutation({
    // Mention ids are re-derived from the FINAL text at submit (rather than
    // tracked in state) so a draft restored from the local store — which
    // never went through an onChange — still notifies its @-tagged members.
    mutationFn: (text: string) =>
      api.createComment(
        card.id,
        text,
        deriveMemberMentionIds(text, members ?? []),
      ),
    onSuccess: () => {
      setBody('')
      clearDraft()
      invalidateAll()
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteComment(id),
    onSuccess: invalidateAll,
  })

  const edit = useMutation({
    mutationFn: ({ id, body: next }: { id: string; body: string }) =>
      api.updateComment(id, next),
    onSuccess: invalidateAll,
  })

  const editNote = useCallback(
    async (commentId: string, nextBody: string) => {
      await edit.mutateAsync({ id: commentId, body: nextBody })
    },
    [edit],
  )

  return (
    <section data-kanban-focus="notes">
      {/* "Card notes" (not just "Comments") to disambiguate from the Teams Hub
          "Team chat" thread that can render above this in the drawer — these
          are the per-card notes the card-face badge counts
          (BUG-2026-07-02-KANBAN-COMMENTS-INVISIBLE). */}
      <h3 className="mb-1 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
        <MessageSquare className="h-3 w-3" /> Card notes
        {q.data && q.data.length > 0 && (
          <span className="ml-0.5 font-normal text-neutral-400">· {q.data.length}</span>
        )}
      </h3>
      <div className="mb-2">
        {/* @mention-aware composer — same MentionTextarea + member directory
            the Description editor and Team chat use, so tagging behaves
            identically on every drawer text surface (audit rank 13 parity). */}
        <MentionTextarea
          value={body}
          onChange={(v) => {
            setBody(v)
            setDraft(v)
          }}
          members={members ?? []}
          onSubmit={() => {
            if (body.trim()) post.mutate(body.trim())
          }}
          placeholder="Write a comment…  @ to tag a teammate,  Cmd+Enter to send"
          rows={2}
          ariaLabel="Write a card note"
          className="w-full resize-none rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
        />
        <div className="mt-1 flex items-center justify-end gap-2">
          {/* Manual draft purge — shown only when there's something to clear. */}
          {body.trim() && (
            <button
              type="button"
              onClick={() => {
                setBody('')
                clearDraft()
              }}
              className="text-[11px] font-medium text-neutral-400 transition-colors hover:text-neutral-600 dark:hover:text-neutral-300"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={() => body.trim() && post.mutate(body.trim())}
            disabled={!body.trim() || post.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {post.isPending && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
            Post
          </button>
        </div>
      </div>
      {q.isLoading && <p className="text-xs text-neutral-400">Loading…</p>}
      {/* BUG-2026-07-07-KANBAN-CARD-404-TOAST-SPAM — surface an error state
          instead of an endless "Loading…" when notes can't be fetched (e.g.
          an archived card whose lane was soft-deleted). */}
      {q.isError && !q.isLoading && (
        <p className="text-xs text-neutral-400" role="status">
          Notes are unavailable for this card.
        </p>
      )}
      {q.data && q.data.length === 0 && (
        <p className="text-xs text-neutral-400">No notes yet.</p>
      )}
      <ul className="space-y-1.5">
        {q.data?.slice().reverse().map((c) => (
          <CommentNoteRow
            key={c.id}
            comment={c}
            members={members ?? []}
            resolveMember={resolveMember}
            currentUserId={currentUserId}
            onEdit={editNote}
            onDelete={(id) => remove.mutate(id)}
            deletePending={remove.isPending}
          />
        ))}
      </ul>
    </section>
  )
}
